import { useNavigate, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect, useRef } from "react";
import routes from "./config";
import { ensurePixelForPublicPage, ensurePixelForCheckout, trackPageView, isPixelAllowedOnPath } from "@/lib/metaPixel";
import { ensureGtmForPublicPage, ensureGtmForDoctorArea, pushGtmDoctorPageView, isGtmAllowedOnDoctorArea, setGtmTrackingAllowed } from "@/lib/gtm";
import { ensureClarity, stopClarity, clarityAllowedOnPath } from "@/lib/clarity";
import { useLocation } from "react-router-dom";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  // PageView: só em rotas públicas de marketing, uma vez por mudança REAL de
  // rota (não em re-render/atualização de componente) — nunca em áreas
  // autenticadas/clínicas e nunca no /checkout (que só dispara checkout_viewed).
  useEffect(() => {
    // Inclui o `search`: `/paciente?modo=cadastro` é PÚBLICO (cadastro) mas
    // `/paciente` é área logada — sem a query na chave, a troca entre os dois
    // não reclassificava e o cadastro ficava sem tracking (pedido da agência).
    const chaveRota = location.pathname + location.search;
    if (lastTrackedPath.current === chaveRota) return;
    lastTrackedPath.current = chaveRota;

    // Nesta SPA o container do GTM, uma vez carregado, continua ativo ao trocar
    // de rota — então "não carregar" não basta para as rotas que levam id de
    // paciente no path (ex.: /medico/prontuario/1214, de onde a tag GA4 da
    // agência enviava `dl=` com o id). Aqui silenciamos/religamos o envio a
    // cada navegação, antes de qualquer PageView.
    const trackingAllowed =
      isPixelAllowedOnPath(location.pathname, location.search) !== "blocked" ||
      isGtmAllowedOnDoctorArea(location.pathname);
    setGtmTrackingAllowed(trackingAllowed);

    const mode = isPixelAllowedOnPath(location.pathname, location.search);
    // Clarity grava o site todo, menos rotas de consulta (sala, espera, chat e aba Consultas do /admin) — ver lib/clarity.ts.
    if (clarityAllowedOnPath(location.pathname, mode === "public", location.search)) ensureClarity(); else stopClarity();
    if (mode === "public") {
      ensurePixelForPublicPage();
      ensureGtmForPublicPage();
      trackPageView();
    } else if (mode === "checkout") {
      ensurePixelForCheckout();
      // GTM também precisa carregar aqui — pedido do time de marketing
      // (18/09/2026): o evento "purchase" agora dispara em /checkout, na
      // exibição do QR Code do PIX ou aprovação do cartão (ver
      // PagarMePayment.tsx). Sem isso, pushGtmPurchase escrevia no
      // window.dataLayer mas o container GTM nunca tinha sido injetado nessa
      // rota — nada era lido pelo Tag Assistant.
      ensureGtmForPublicPage();
      // Sem PageView aqui — /checkout só dispara checkout_viewed explicitamente.
    } else if (isGtmAllowedOnDoctorArea(location.pathname)) {
      // Área do médico (pedido da agência, 24/09/2026): GTM carrega em todas as
      // páginas /medico. O Meta Pixel continua BLOQUEADO aqui — só o GTM entra.
      // O PageView vai com a URL mascarada por safeTrackingUrl(), porque
      // /medico/prontuario/:patientId expõe o id do paciente na URL.
      ensureGtmForDoctorArea(location.pathname);
      pushGtmDoctorPageView(location.pathname);
    }
  }, [location.pathname, location.search]);

  return element;
}
